import { createContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";

import {
  editCategoryItemService,
  createCategoryItemService,
  deleteCategoryItemService,
  getCategoryItemsService,
  getCategoryService,
} from "./../../../services/userServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "./../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";

export const categoryAdminContext = createContext({
  categoryItems: [],
  setCategoryItems: () => { },
  added_cities: [],
  setAdded_cities: () => { },
  category: {},
  setCategory: () => { },
  search: "",
  setSearch: () => { },
  id: "",
  setId: () => { },
  icon: "",
  setIcon: () => { },
  description: "",
  setDescription: () => { },
  is_active: null,
  setIs_active: () => { },
  name: "",
  setName: () => { },
  rank: "",
  setRank: () => { },
  smallIcon: "",
  setSmallIcon: () => { },
  isShowModal: null,
  setIsShowModal: () => { },
  isShowDetailsModal: null,
  setIsShowDetailsModal: () => { },
  handleShowModal: () => { },
  handleCloseModal: () => { },
  handleShowDetailsModal: () => { },
  handleCloseDetailsModal: () => { },
  handleGetCategoryItemList: () => { },
  handleGetCategoryItem: () => { },
  handleCreateCategoryItem: () => { },
  handleEditCategoryItem: () => { },
  handleDeleteCategoryItem: () => { },
});

const CategoryAdminContext = ({ children }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const cities = useSelector((state) => state.cities);

  const [categoryItems, setCategoryItems] = useState([]);
  const [added_cities, setAdded_cities] = useState([]);
  const [category, setCategory] = useState({});
  const [search, setSearch] = useState("");
  const [id, setId] = useState("");
  const [icon, setIcon] = useState("");
  const [description, setDescription] = useState("");
  const [is_active, setIs_active] = useState(false);
  const [name, setName] = useState("");
  const [rank, setRank] = useState("");
  const [smallIcon, setSmallIcon] = useState("");
  const [isShowModal, setIsShowModal] = useState(false);
  const [isShowDetailsModal, setIsShowDetailsModal] = useState(false);

  const handleResetState = () => {
    setId("");
    setRank("");
    setSmallIcon("");
    setName("");
    setIcon("");
    setDescription("");
    setIs_active(false);
    setAdded_cities([]);
  };

  // Create Category Modal Operations
  const handleShowModal = (catValue) => {
    setIsShowModal(true);
    if (catValue) {
      setId(catValue.id);
      setRank(catValue.rank);
      setSmallIcon(catValue.small_icon);
      setName(catValue.name);
      setIcon(catValue.icon);
      setIs_active(catValue.is_active);
      setDescription(catValue.description);
      setAdded_cities(
        catValue.city?.map((_item) => ({ label: _item.name, value: _item.id }))
      );
    }
  };
  const handleCloseModal = () => {
    setIsShowModal(false);
    handleResetState();
  };

  // Details Category Modal Operations
  const handleShowDetailsModal = (value) => {
    setIsShowDetailsModal(true);
    setCategory(value);
  };
  const handleCloseDetailsModal = () => {
    setIsShowDetailsModal(false);
    setCategory({});
  };

  // Handle Get Category Item List Method
  const handleGetCategoryItemList = async (event) => {
    event?.preventDefault();
    const objData = { search };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getCategoryItemsService(objData);
      if (status === 200) {
        setCategoryItems(data.results);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get Category Item Method
  const handleGetCategoryItem = async (id, func) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getCategoryService(id);
      if (status === 200) {
        // Handle Custom Function
        if (func) {
          func(data);
        }
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create Category Item Method
  const handleCreateCategoryItem = async (event) => {
    event?.preventDefault();

    const objData = {
      name,
      description,
      is_active,
      icon,
      rank: Number(rank),
      small_icon: smallIcon,
      added_cities: added_cities.map((_item) => _item.value),
      deleted_cities: [],
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createCategoryItemService(objData);
      if (status === 201) {
        toastSuccessMessage("آیتم با موفقیت ایجاد شد");
        handleGetCategoryItemList();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Category Item Method
  const handleEditCategoryItem = async (event) => {
    event?.preventDefault();

    const deleted_cities = cities
      .filter((_c) => {
        const isExist = !isEmpty(
          added_cities.find((_nc) => _nc.value === _c.value)
        );
        return !isExist;
      })
      .map((_item) => _item.value);

    const objData = {
      id,
      name,
      description,
      is_active,
      icon,
      rank: Number(rank),
      small_icon: smallIcon,
      added_cities: added_cities.map((_item) => _item.value),
      deleted_cities,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editCategoryItemService(objData);
      if (status === 200) {
        toastSuccessMessage("آیتم با موفقیت ویرایش شد");
        handleGetCategoryItemList();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete Category Item Method
  const handleDeleteCategoryItem = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteCategoryItemService(id);
      if (status === 204) {
        toastSuccessMessage(`آیتم  ${id} با موفقیت حذف شد`);
        handleGetCategoryItemList();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <categoryAdminContext.Provider
      value={{
        categoryItems,
        setCategoryItems,
        added_cities,
        setAdded_cities,
        category,
        setCategory,
        search,
        setSearch,
        id,
        setId,
        icon,
        setIcon,
        description,
        setDescription,
        is_active,
        setIs_active,
        name,
        setName,
        rank,
        setRank,
        smallIcon,
        setSmallIcon,
        isShowModal,
        setIsShowModal,
        isShowDetailsModal,
        setIsShowDetailsModal,
        handleShowDetailsModal,
        handleCloseDetailsModal,
        handleShowModal,
        handleCloseModal,
        handleGetCategoryItemList,
        handleGetCategoryItem,
        handleCreateCategoryItem,
        handleEditCategoryItem,
        handleDeleteCategoryItem,
      }}
    >
      {children}
    </categoryAdminContext.Provider>
  );
};

export default CategoryAdminContext;
